import { useEffect, useState } from "react";
import { Box, Button, IconButton, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography } from "@mui/material";
import { AddCircleOutline, DeleteOutline, Checklist } from "@mui/icons-material";
import { postData } from "../../services/base.service";
import { useNotification } from "../Shared/NotificationProvider";
import { ProjectDetail, ProjectRfsItem } from "../../types/projectTypes";
import { sameRfsName } from "../timesheet/timesheetUtils";

type ProjectRfsTabProps = {
    detail: ProjectDetail;
    isEditMode: boolean;
    setLoading: Function;
    refresh: () => Promise<void>;
};

// RfS (subtasks) of the project, identified by name only (unique per project). Changes are saved
// immediately; staff pick the RfS - or create a new one - when they book time on this project.
function ProjectRfsTab({ detail, isEditMode, setLoading, refresh }: ProjectRfsTabProps) {
    const notify = useNotification();
    const projectId = detail.project.ID;
    const [rows, setRows] = useState<ProjectRfsItem[]>(detail.rfs);
    const [newName, setNewName] = useState('');
    const [newCode, setNewCode] = useState('');

    useEffect(() => { setRows(detail.rfs); }, [detail.rfs]);

    const duplicateOf = (name: string, exceptId?: number) => detail.rfs.find((r) => r.id !== exceptId && sameRfsName(r.name, name)) ?? null;
    const newDuplicate = newName.trim() ? duplicateOf(newName) : null;

    const run = async (action: () => Promise<unknown>, success?: string) => {
        setLoading(true);
        try {
            await action();
            if (success) notify(success, 'success');
            await refresh();
            return true;
        } catch (error) {
            notify((error as Error).message, 'error');
            setRows(detail.rfs); // undo the local edit
            return false;
        } finally {
            setLoading(false);
        }
    };

    const add = async () => {
        const name = newName.trim();
        if (!name) { notify('Enter the RfS name.', 'warning'); return; }
        if (newDuplicate) { notify(`An RfS named "${newDuplicate.name}" already exists on this project.`, 'warning'); return; }
        if (await run(() => postData('projects/rfs/add', { projectId, name, code: newCode.trim() || undefined }), `RfS "${name}" added.`)) { setNewName(''); setNewCode(''); }
    };

    const saveRow = (row: ProjectRfsItem) => {
        const before = detail.rfs.find((r) => r.id === row.id);
        const name = row.name.trim();
        const code = (row.code ?? '').trim();
        if (!before || (before.name === name && (before.code ?? '') === code)) return;
        if (!name) { notify('The RfS name cannot be empty.', 'warning'); setRows(detail.rfs); return; }
        const duplicate = duplicateOf(name, row.id);
        if (duplicate) { notify(`An RfS named "${duplicate.name}" already exists on this project.`, 'warning'); setRows(detail.rfs); return; }
        run(() => postData('projects/rfs/update', { id: row.id, name, code }));
    };

    const remove = (row: ProjectRfsItem) => {
        if (!window.confirm(`Remove RfS "${row.name}"?`)) return;
        run(() => postData('projects/rfs/remove', { id: row.id }), `RfS "${row.name}" removed.`);
    };

    return (
        <Box paddingTop={2}>
            <Stack direction="row" alignItems="center" gap={1} sx={{ marginBottom: 1 }}>
                <Checklist /><Typography variant="h6">RfS (subtasks)</Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" gutterBottom>
                Staff can book their time on an RfS (optional) and create new ones from the timesheet. Each name exists only once per project;
                the RfS name goes into the entry&apos;s description automatically.
            </Typography>

            <Table size="small" sx={{ marginTop: 1 }}>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ width: 160, fontWeight: 600 }}>RfS code</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>RfS name</TableCell>
                        <TableCell align="right" sx={{ width: 120, fontWeight: 600 }}>Booked hours</TableCell>
                        {isEditMode && <TableCell sx={{ width: 48 }} />}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={isEditMode ? 4 : 3} sx={{ color: 'text.secondary' }}>
                                No RfS yet{isEditMode ? ' - add the first one below.' : ' - switch to Edit to add them, or let staff create them in the timesheet.'}
                            </TableCell>
                        </TableRow>
                    )}
                    {rows.map((row) => (
                        <TableRow key={row.id}>
                            <TableCell>
                                {isEditMode ? (
                                    <TextField variant="standard" size="small" fullWidth value={row.code ?? ''} inputProps={{ maxLength: 50 }} placeholder="Code"
                                        onChange={(e) => setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, code: e.target.value } : r)))}
                                        onBlur={() => saveRow(row)} />
                                ) : (row.code || '-')}
                            </TableCell>
                            <TableCell>
                                {isEditMode ? (
                                    <TextField variant="standard" size="small" fullWidth value={row.name} inputProps={{ maxLength: 255 }}
                                        onChange={(e) => setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, name: e.target.value } : r)))}
                                        onBlur={() => saveRow(row)} />
                                ) : row.name}
                            </TableCell>
                            <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: 'text.secondary' }}>{row.bookedHours || '-'}</TableCell>
                            {isEditMode && (
                                <TableCell>
                                    <Tooltip title={row.bookedHours ? 'Has booked time - rename it instead' : 'Remove'}>
                                        <span>
                                            <IconButton size="small" disabled={row.bookedHours > 0} onClick={() => remove(row)}><DeleteOutline fontSize="small" /></IconButton>
                                        </span>
                                    </Tooltip>
                                </TableCell>
                            )}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            {isEditMode && (
                <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5} alignItems={{ sm: 'flex-start' }} sx={{ marginTop: 2 }}>
                    <TextField size="small" label="RfS code (optional)" value={newCode} onChange={(e) => setNewCode(e.target.value)} inputProps={{ maxLength: 50 }}
                        sx={{ width: { sm: 180 } }} onKeyDown={(e) => { if (e.key === 'Enter') add(); }} helperText=" " />
                    <TextField size="small" label="New RfS name" value={newName} onChange={(e) => setNewName(e.target.value)} inputProps={{ maxLength: 255 }}
                        sx={{ flexGrow: 1 }} onKeyDown={(e) => { if (e.key === 'Enter') add(); }}
                        error={!!newDuplicate} helperText={newDuplicate ? `"${newDuplicate.name}" already exists on this project.` : ' '} />
                    <Button variant="outlined" startIcon={<AddCircleOutline />} onClick={add} disabled={!!newDuplicate} sx={{ marginTop: { sm: 0.25 } }}>Add RfS</Button>
                </Stack>
            )}
        </Box>
    );
}

export default ProjectRfsTab;
